import type { FC } from 'react';
import { lazy } from 'react';
import { useParams } from 'react-router';
import { useLngMarketingRoot } from 'src/apis/lng-marketing/use-lng-marketing-root';
import { useLngOfferDetails } from 'src/apis/lng-marketing/use-lng-offer-details';
import { useLngOfferDocumentFile } from 'src/apis/lng-marketing/use-lng-offer-document-file';
import { useLngOffers } from 'src/apis/lng-marketing/use-lng-offers';
import { useOwnTerminals } from 'src/apis/lng-marketing/use-own-terminals';
import { useOrganisationDetails } from 'src/apis/organisation-service/use-organisation-details';
import type { OptionalOrganisationServiceRequest } from 'src/apis/organisation-service/use-organisation-service';
import { PageSpinner } from 'src/components/spinner-container';
import { lngOffersEmptyParams } from 'src/pages/lng/offers/utils';
import { Forbidden } from 'src/utils/errors';

const OfferCopyForm = lazy(() => import('./offer-copy-form'));

type Props = {
  organisationService: OptionalOrganisationServiceRequest;
};

export const LngOfferCopy: FC<Props> = ({ organisationService }) => {
  const { offerId } = useParams<{ offerId: string }>();

  if (!organisationService.response?.data._links.getMyOrganisationDetails) {
    throw new Forbidden();
  }

  const lngMarketingRoot = useLngMarketingRoot();
  const lngOffers = useLngOffers({
    lngMarketingRoot,
    params: lngOffersEmptyParams,
  });
  const organisationDetails = useOrganisationDetails({
    url: organisationService.response.data._links.getMyOrganisationDetails.href,
  });
  const lngOfferDetails = useLngOfferDetails({ lngOffers, offerId });
  const ownTerminals = useOwnTerminals({ lngMarketingRoot });
  const pdfAttachment = useLngOfferDocumentFile({ lngOfferDetails });

  if (
    lngMarketingRoot.response === null ||
    lngOffers.response === null ||
    lngOfferDetails.response === null ||
    ownTerminals.response === null ||
    pdfAttachment?.response === null ||
    organisationDetails.response === null
  )
    return <PageSpinner />;

  return (
    <OfferCopyForm
      lngMarketingRoot={lngMarketingRoot}
      lngOfferDetails={lngOfferDetails}
      ownTerminals={ownTerminals}
      pdfAttachment={pdfAttachment}
      lngOffers={lngOffers}
      organisationDetails={organisationDetails}
    />
  );
};
